"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { JarvisToast } from "@components/jarvis-ui";
import { FaFileExcel, FaFileLines, FaFilePdf, FaUpload } from "react-icons/fa6";
import axiosInstance from "@libs/axios";
import { useTheme } from "@contexts/ThemeContext";

const { Thead, Tbody, Tr, Th, Td } = Table;

// Backend contract: GET /api/v1/ceisa/edit/document/by-batch-uuid/:batchUUID
// returns []CeisaEditDocumentDto. Documented fields are id +
// documentTypeCode + documentNo + fileUrl + fileName + urlDokumen, but the
// payload may carry extras — keep the type permissive.
type CeisaEditDocument = {
  id: number;
  documentTypeCode?: string;
  documentNo?: string;
  fileUrl?: string;
  fileName?: string;
  urlDokumen?: string | null;
};

type UnggahBerkasModalProps = {
  opened: boolean;
  onClose: () => void;
  batchUUID: string;
};

const fileExt = (url: string, name: string): string => {
  const path = (() => {
    try {
      return new URL(url).pathname;
    } catch {
      return url.split("?")[0];
    }
  })();
  const source = name || path;
  return (source.split(".").pop() || "").toLowerCase();
};

const FileIcon = ({ url, name }: { url: string; name: string }) => {
  const ext = fileExt(url, name);
  const isExcel = ext === "xlsx" || ext === "xls" || ext === "csv";
  const isPdf = ext === "pdf";
  const Icon = isExcel ? FaFileExcel : isPdf ? FaFilePdf : FaFileLines;
  const color = isExcel ? "#16A34A" : isPdf ? "#DC2626" : "#475569";
  return <Icon size={16} color={color} />;
};

export default function UnggahBerkasModal({
  opened,
  onClose,
  batchUUID,
}: UnggahBerkasModalProps) {
  const { colors } = useTheme();
  const [docs, setDocs] = useState<CeisaEditDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadingId, setUploadingId] = useState<number | null>(null);

  // One shared hidden input — `pendingDocIdRef` stashes which row clicked,
  // so the change handler can route the file to the correct edit-doc.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingDocIdRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!batchUUID) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/api/v1/ceisa/edit/document/by-batch-uuid/${encodeURIComponent(batchUUID)}`
      );
      const payload = res.data?.data ?? res.data;
      setDocs(Array.isArray(payload) ? payload : []);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        "Gagal memuat daftar dokumen";
      JarvisToast.error(msg);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [batchUUID]);

  useEffect(() => {
    if (opened) refresh();
  }, [opened, refresh]);

  const triggerUpload = (docId: number) => {
    pendingDocIdRef.current = docId;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    const docId = pendingDocIdRef.current;
    pendingDocIdRef.current = null;
    if (!picked || docId == null) return;

    const lower = picked.name.toLowerCase();
    const allowed =
      lower.endsWith(".pdf") ||
      lower.endsWith(".xlsx") ||
      lower.endsWith(".xls") ||
      lower.endsWith(".csv");
    if (!allowed) {
      JarvisToast.error(
        "Hanya file PDF atau Excel (.xlsx/.xls/.csv) yang diperbolehkan.",
        "Format tidak didukung"
      );
      return;
    }

    try {
      setUploadingId(docId);
      const fd = new FormData();
      fd.append("file", picked);
      await axiosInstance.post(
        `/api/v1/ceisa/edit/document/${docId}/file`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      JarvisToast.success(`${picked.name} terupload`);
      // Re-fetch so fileUrl/fileName reflect the freshly uploaded file.
      await refresh();
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        "Gagal mengupload file";
      JarvisToast.error(msg);
    } finally {
      setUploadingId(null);
    }
  };

  const handleDownload = async (url: string, name: string) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name || `document.${fileExt(url, name) || "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} size="lg" style={{ color: colors.text }}>
          Unggah Berkas
        </Text>
      }
      size="xl"
      centered
      styles={{
        content: { backgroundColor: colors.card, border: `1px solid ${colors.border}` },
        header: { backgroundColor: colors.card, borderBottom: `1px solid ${colors.border}` },
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.xlsx,.xls,.csv,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            Batch UUID: {batchUUID || "—"}
          </Text>
          <Button size="xs" variant="light" onClick={refresh} loading={loading}>
            Refresh
          </Button>
        </Group>

        <ScrollArea h={420} type="auto">
          <Table striped highlightOnHover stickyHeader withTableBorder>
            <Thead>
              <Tr>
                <Th style={{ width: 80, textAlign: "center" }}>Seri</Th>
                <Th>Jenis</Th>
                <Th>No. Dokumen</Th>
                <Th>File</Th>
                <Th style={{ width: 110, textAlign: "center" }}>Aksi</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading && docs.length === 0 ? (
                <Tr>
                  <Td colSpan={5}>
                    <Group justify="center" py="lg">
                      <Loader size="sm" />
                      <Text size="xs" c="dimmed">Memuat dokumen...</Text>
                    </Group>
                  </Td>
                </Tr>
              ) : docs.length === 0 ? (
                <Tr>
                  <Td colSpan={5}>
                    <Text ta="center" size="xs" c="dimmed" py="lg">
                      Belum ada dokumen pada batch ini.
                    </Text>
                  </Td>
                </Tr>
              ) : (
                docs.map((d, idx) => {
                  const url = (d.fileUrl || "").trim();
                  const name = (d.fileName || "").trim();
                  return (
                    <Tr key={d.id ?? idx}>
                      <Td style={{ textAlign: "center" }}>
                        <Text size="xs">{idx + 1}</Text>
                      </Td>
                      <Td>
                        <Text size="xs" fw={500}>
                          {d.documentTypeCode || "-"}
                        </Text>
                      </Td>
                      <Td>
                        <Text size="xs" style={{ wordBreak: "break-word" }}>
                          {d.documentNo || "-"}
                        </Text>
                      </Td>
                      <Td>
                        {url ? (
                          <Tooltip label={name || "Download"} withArrow position="top">
                            <Group
                              gap={6}
                              wrap="nowrap"
                              style={{ cursor: "pointer", maxWidth: 220 }}
                              onClick={() => handleDownload(url, name)}
                            >
                              <FileIcon url={url} name={name} />
                              <Text
                                size="xs"
                                style={{
                                  textDecoration: "underline",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {name || "Download"}
                              </Text>
                            </Group>
                          </Tooltip>
                        ) : (
                          <Badge size="xs" color="gray" variant="light">
                            Belum ada file
                          </Badge>
                        )}
                      </Td>
                      <Td style={{ textAlign: "center" }}>
                        <Tooltip label={url ? "Ganti file" : "Unggah file"} withArrow>
                          <ActionIcon
                            size="md"
                            variant="subtle"
                            color="blue"
                            loading={uploadingId === d.id}
                            onClick={() => triggerUpload(d.id)}
                          >
                            <FaUpload size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </ScrollArea>

        <Group justify="flex-end" gap="sm">
          <Button variant="outline" onClick={onClose}>Tutup</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
